import { useMemo } from "react";
import { observer } from "mobx-react-lite";
import { Skeleton } from "@/components/ui/skeleton";
import { dashboardEChartsStore } from "@/features/esg-dashboard-v3/stores/e-charts.store";
import { dashboardEChartsTotalsStore } from "@/features/esg-dashboard-v3/stores/e-charts-totals.store";
import { formatValue } from "@/features/esg-dashboard-v3/domains/formatting";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  buildReportingPeriodLabel,
  buildTotalsChartData,
} from "./emissions-totals-chart.utils";

const formatYAxis = (value) => {
  if (value === 0) return "0";
  return `${value / 1000}K`;
};

const CHART_MARGIN = { top: 10, right: 10, left: 8, bottom: 0 };
const Y_AXIS_WIDTH = 64;

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;

  const orderedPayload = ["total", "scope1", "scope2", "scope3"]
    .map((key) => payload.find((item) => item.dataKey === key))
    .filter(Boolean);

  return (
    <div className="rounded-xl border bg-card p-4 shadow-xl">
      <p className="mb-3 text-xs font-bold uppercase tracking-wider text-slate-400">
        {`Year ${label}`}
      </p>

      <div className="space-y-2 text-xs">
        {orderedPayload.map((item) => (
          <div
            key={item.dataKey}
            className="flex items-center justify-between gap-8"
          >
            <span className="flex items-center gap-2">
              <span
                className="h-2.5 w-2.5 rounded-full"
                style={{ backgroundColor: item.color }}
              />
              {item.name}
            </span>

            <span className="font-semibold">{`${formatValue(item.value)} tCO2e`}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const CustomCardSkeleton = () => <Skeleton className="h-[400px] w-full" />;

const EmissionTrendCard = observer(({ theme = "light" }) => {
  const totalsStore = dashboardEChartsTotalsStore;
  const chartsStore = dashboardEChartsStore;
  const activeYears = chartsStore.activeYears;
  const isFirstLoad = !totalsStore.hasLoadedOnce;

  const data = useMemo(
    () => buildTotalsChartData(totalsStore.summary, activeYears),
    [totalsStore.summary, activeYears],
  );
  const reportingPeriodLabel = useMemo(
    () => buildReportingPeriodLabel(activeYears),
    [activeYears],
  );

  const colors = {
    total: "#06B6D4",
    scope1: "#1e40af",
    scope2: "#3b82f6",
    scope3: "#93c5fd",
    grid: theme === "light" ? "#e2e8f0" : "#334155",
    text: theme === "light" ? "#64748b" : "#94a3b8",
  };

  return (
    <div className="rounded-md border bg-card/95 p-6 transition-all sm:p-8">
      <div className="mb-6">
        <h3 className="text-[17px] text-base font-bold">
          Emissions trend by scope
        </h3>
        <p className="mt-1 text-xs text-muted-foreground">
          {`Company-wide total and per-scope emissions across ${reportingPeriodLabel} · tCO2e`}
        </p>
      </div>

      <div className="mb-8 flex flex-wrap items-center gap-4 text-xs font-bold">
        <div className="flex items-center gap-2">
          <span
            className="h-2.5 w-2.5 rounded-full"
            style={{ backgroundColor: colors.total }}
          />
          <span>Total</span>
        </div>

        <div className="flex items-center gap-2">
          <span
            className="h-2.5 w-2.5 rounded-full"
            style={{ backgroundColor: colors.scope1 }}
          />
          <span>Scope 1</span>
        </div>

        <div className="flex items-center gap-2">
          <span
            className="h-2.5 w-2.5 rounded-full"
            style={{ backgroundColor: colors.scope2 }}
          />
          <span>Scope 2</span>
        </div>

        <div className="flex items-center gap-2">
          <span
            className="h-2.5 w-2.5 rounded-full"
            style={{ backgroundColor: colors.scope3 }}
          />
          <span>Scope 3</span>
        </div>
      </div>

      <div className="h-[360px] w-full">
        {isFirstLoad ? (
          <CustomCardSkeleton />
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data} margin={CHART_MARGIN}>
              <CartesianGrid
                vertical={false}
                strokeDasharray="3 3"
                stroke={colors.grid}
              />

              <XAxis
                dataKey="year"
                tickLine={false}
                axisLine={false}
                stroke={colors.text}
                dy={12}
                style={{ fontSize: 11, fontWeight: 600 }}
              />

              <YAxis
                width={Y_AXIS_WIDTH}
                tickFormatter={formatYAxis}
                tickLine={false}
                axisLine={false}
                stroke={colors.text}
                style={{ fontSize: 11, fontWeight: 600 }}
              />

              <Tooltip content={<CustomTooltip />} />

              <Line
                type="linear"
                dataKey="scope3"
                name="Scope 3"
                stroke={colors.scope3}
                strokeWidth={2}
                dot={{ r: 3, fill: colors.scope3, strokeWidth: 0 }}
                activeDot={{ r: 5 }}
              />

              <Line
                type="linear"
                dataKey="scope2"
                name="Scope 2"
                stroke={colors.scope2}
                strokeWidth={2}
                dot={{ r: 3, fill: colors.scope2, strokeWidth: 0 }}
                activeDot={{ r: 5 }}
              />

              <Line
                type="linear"
                dataKey="scope1"
                name="Scope 1"
                stroke={colors.scope1}
                strokeWidth={2}
                dot={{ r: 3, fill: colors.scope1, strokeWidth: 0 }}
                activeDot={{ r: 5 }}
              />

              <Line
                type="linear"
                dataKey="total"
                name="Total"
                stroke={colors.total}
                strokeWidth={3}
                dot={{ r: 4, fill: colors.total, strokeWidth: 0 }}
                activeDot={{ r: 7 }}
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
});

export default EmissionTrendCard;
